import { useMutation } from '@apollo/client/react'
import { AutoComplete, Divider, Input, Row, Tag } from 'antd'
import { useState } from 'react'
import { tabFields } from '~app/configuration/labels'
import {
  type DocumentFragment,
  UpdateDocumentTagsDocument,
} from '~app/graph/generated/graphql'
import { useAutoCompleteField } from '~app/hooks/useField'
import i18n from '~app/i18n/i18n'
import useGetDocumentTags from './useGetDocumentTags'
import { renderScanId } from './utils'

interface Props {
  document: DocumentFragment
  onClose: () => any
}

const DocumentTags = ({ document }: Props) => {
  const [updateTags] = useMutation(UpdateDocumentTagsDocument)
  const { allAvailableTags } = useGetDocumentTags()
  const [savingTags, setSavingTags] = useState(false)
  const {
    value,
    setValue,
    inputField: { ...inputFieldProps },
    focus,
  } = useAutoCompleteField('')

  const saveInputValue = newValue => {
    const { id } = document
    const tags = document.tags || []
    if (
      newValue !== '' &&
      newValue !== null &&
      !tags.includes(newValue) &&
      allAvailableTags.some(tag => tag === newValue)
    ) {
      setSavingTags(true)
      updateTags({ variables: { id, tags: [...tags, newValue] } }).finally(
        () => {
          setValue('')
          setSavingTags(false)
          focus()
        }
      )
    }
  }

  return (
    <>
      <Divider
        orientation="left"
        orientationMargin={0}
        style={{ margin: '8px 0' }}
      >
        Tags
      </Divider>
      <Row style={{ marginBottom: 8 }}>
        <AutoComplete
          {...inputFieldProps}
          value={value || ''}
          filterOption
          dataSource={allAvailableTags}
          disabled={savingTags}
          tabIndex={tabFields.tags}
          placeholder={i18n.t('documentView.addTag')}
          onSelect={saveInputValue}
        >
          <Input
            onPressEnter={e => {
              e.stopPropagation()
              saveInputValue(value)
            }}
          />
        </AutoComplete>
      </Row>
      <Row>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {document.tags.map(tag => (
            <Tag
              key={tag}
              closable
              onClose={() => {
                const tags = document.tags || []
                if (tags.includes(tag)) {
                  setSavingTags(true)
                  updateTags({
                    variables: {
                      id: document.id,
                      tags: [...tags].filter(item => item !== tag),
                    },
                  }).finally(() => {
                    setSavingTags(false)
                  })
                }
              }}
            >
              {tag.toUpperCase()}
            </Tag>
          ))}
        </div>
      </Row>
      <div style={{ marginTop: 8, float: 'right' }}>
        <Tag color="orange">{document.scanCenter}</Tag>
        {renderScanId(document) && (
          <Tag color="green">{renderScanId(document)}</Tag>
        )}
      </div>
    </>
  )
}

export default DocumentTags
